import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { Button } from '@helpcenter/analytics-components';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { AsyncLoadedPasswordStrengthMeter } from 'view/common/password-strength-meter';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import WatchIcon from '@atlaskit/icon/core/eye-open';
import WatchFilledIcon from '@atlaskit/icon/core/eye-open-filled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ErrorIndication } from '../error-indication';
import messages from './messages';

/**
 * Feature gate: a11y_password_show_button_focus_indicator
 *
 * Adds a clearly visible keyboard focus indicator to the show/hide password
 * toggle. The toggle renders inside the @atlaskit/textfield's `elemAfterInput`
 * slot, whose container has `overflow: hidden`, which clips the button's default
 * (outer) focus ring - leaving no visible focus indicator (fails WCAG 2.4.7).
 * When enabled, an inset focus ring is drawn instead so it is not clipped.
 */
const PASSWORD_SHOW_BUTTON_FOCUS_INDICATOR = 'a11y_password_show_button_focus_indicator';

const isPasswordShowButtonFocusIndicatorEnabled = (): boolean =>
	FeatureGates.checkGate(PASSWORD_SHOW_BUTTON_FOCUS_INDICATOR);

interface PasswordFieldProps {
	id: string;
	maxLength?: number;
	minLength?: number;
	value?: string;
	testId?: string;
	onChange: (updatedPassword: string) => void;
	isDisabled: boolean;
	autoFocus?: boolean;
	weakPasswords?: string[];
	errorMessage?: string;
	isInvalid: boolean;
	hideStrengthMeter?: boolean;
	onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

interface PasswordFieldState {
	showPassword: boolean;
}

export class PasswordField extends React.Component<
	PasswordFieldProps & WrappedComponentProps,
	PasswordFieldState
> {
	state = {
		showPassword: false,
	};

	onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		this.props.onChange(e.target.value);
	};

	onShowPasswordToggle = () => {
		this.setState((prevState) => ({
			showPassword: !prevState.showPassword,
		}));
	};

	renderShowHideButton = () => {
		const { intl, isDisabled } = this.props;
		const { showPassword } = this.state;

		const buttonActionPrefix = showPassword ? 'hide' : 'show';
		const togglePasswordButtonDescription = showPassword
			? intl.formatMessage(messages.hidePassword)
			: intl.formatMessage(messages.showPassword);

		const icon = showPassword ? (
			<WatchFilledIcon
				spacing="spacious"
				testId="watch-filled-icon"
				label={togglePasswordButtonDescription}
			/>
		) : (
			<WatchIcon spacing="spacious" testId="watch-icon" label={togglePasswordButtonDescription} />
		);

		// Behind the a11y gate, render a variant of the toggle that pulls its design-system focus
		// ring inside the button (see ShowHideButton) so the surrounding field's `overflow: hidden`
		// cannot clip it (WCAG 2.4.7).
		const focusIndicatorEnabled = isPasswordShowButtonFocusIndicatorEnabled();
		const ToggleButton = focusIndicatorEnabled ? ShowHideButton : Button;

		return (
			<Tooltip content={togglePasswordButtonDescription}>
				<ToggleButton
					actionSubjectId={`${buttonActionPrefix}-password`}
					appearance="subtle"
					isDisabled={isDisabled}
					onClick={this.onShowPasswordToggle}
					iconBefore={<IconContainer>{icon}</IconContainer>}
					testId={focusIndicatorEnabled ? 'user-password-show-toggle' : undefined}
				/>
			</Tooltip>
		);
	};

	render() {
		const {
			autoFocus,
			intl,
			id,
			maxLength,
			value,
			isDisabled,
			isInvalid,
			weakPasswords,
			errorMessage,
			hideStrengthMeter,
			testId,
			onKeyDown,
		} = this.props;
		const { showPassword } = this.state;
		const passwordFieldType = showPassword ? 'text' : 'password';
		const shouldRenderStrengthMeter = !hideStrengthMeter;

		di(AsyncLoadedPasswordStrengthMeter);

		return (
			<>
				<Textfield
					id={id}
					testId={testId}
					type={passwordFieldType}
					maxLength={maxLength}
					onChange={this.onChange}
					isDisabled={isDisabled}
					autoFocus={autoFocus}
					isInvalid={isInvalid}
					value={value}
					elemAfterInput={this.renderShowHideButton()}
					onKeyDown={onKeyDown}
				/>
				{isInvalid && errorMessage && <ErrorIndication errorMessage={errorMessage} />}
				{shouldRenderStrengthMeter && (
					<AsyncLoadedPasswordStrengthMeter
						intl={intl}
						password={value}
						scoreWords={weakPasswords}
					/>
				)}
			</>
		);
	}
}

export default injectIntl(PasswordField);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const IconContainer = styled.div`
	padding-right: ${token('space.025', '2px')};
	margin-left: ${token('space.negative.025', '-2px')};
`;

/*
 * The show/hide toggle renders inside the @atlaskit/textfield's elemAfterInput slot, whose
 * container has overflow: hidden. The button's design-system focus ring (@atlaskit/focus-ring) is
 * drawn just outside its border box, so the container clips it and keyboard users see no focus
 * indicator (WCAG 2.4.7). A negative outline-offset draws the same DS ring inside the button -
 * matching @atlaskit/focus-ring's own inset variant - so it cannot be clipped. Styling the button
 * directly (className is forwarded to the underlying <button>) avoids adding an extra DOM node; the
 * doubled `&&` wins specificity over the focus-ring rule without needing !important.
 */
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ShowHideButton = styled(Button)`
	&&:focus-visible {
		outline-offset: calc(-1 * ${token('border.width.focused', '2px')});
	}
`;
